import * as React from 'react'
import { unescape } from 'lodash-es'
import { getUserNotificationStore } from '@migrate/stores'
import { UserNotificationType } from '@migrate/stores/UserNotificationStore'

export type DefaultProps<P, DP extends Partial<P>> = Partial<DP> &
  Pick<P, Exclude<keyof P, keyof DP>>

export type ME = React.MouseEvent<HTMLElement>

export type PropsOf<T> =
  T extends React.ComponentType<DefaultProps<infer A, infer B>>
    ? DefaultProps<A, B>
    : T extends React.ComponentType<infer P>
      ? P
      : any

export type ReturnOf<T> = T extends (...args: any[]) => infer R ? R : never

export function equals(origin: any, target: any): boolean {
  const originType = typeof origin
  const targetType = typeof target

  if (originType === targetType) {
    if (originType === 'object') {
      // tslint:disable-next-line
      for (const key in origin) {
        if (!(key in target)) {
          return false
        }

        if (!equals(origin[key], target[key])) {
          return false
        }
      }

      for (const key in target) {
        if (!(key in origin)) {
          return false
        }
      }

      return true
    } else {
      return origin === target
    }
  }

  return false
}

export class InjectableComponent<P, I extends P, S = object> extends React.Component<P, S> {
  get injected() {
    return this.props as I
  }
}

export function isIE() {
  return !!navigator.userAgent.match(/Trident\/7\./)
}

export function routeMessage(
  topic: string,
  message: string,
  callback: () => void = () => {},
  options?: any,
) {
  if (window.self !== window.top) {
    const msg = {
      topic,
      message,
      options,
    }
    window.parent.postMessage(msg, '*')
  } else {
    callback()
  }
}

/**
 * @deprecated Legacy iframe download helper. Prefer `window.location.assign` for direct downloads.
 */
export function downloadURL(url: string, callback?: VoidFunction) {
  const hiddenIFrameID = 'hiddenDownloader'
  let iframe = document.getElementById(hiddenIFrameID) as HTMLIFrameElement
  if (iframe === null) {
    iframe = document.createElement('iframe')
    iframe.id = hiddenIFrameID
    iframe.style.display = 'none'
    document.body.appendChild(iframe)
  }
  iframe.onload = function () {
    try {
      const iframe = document.getElementById(hiddenIFrameID) as HTMLIFrameElement
      const response = iframe.contentDocument?.body.innerText
      if (response && response.length > 10 && response.substring(0, 9) === '{"error":') {
        const json = JSON.parse(response)
        if (json.error) {
          getUserNotificationStore().addNotification(
            UserNotificationType.ERROR,
            unescapeAndHexDecode(json.error),
          )
        }
      }
    } catch {
      // Do nothing.
    } finally {
      if (callback) {
        callback()
      }
    }
    iframe.onerror = function () {
      if (callback) {
        callback()
      }
    }
  }
  iframe.src = url
}

export const defaultFieldMargin = 'normal'
export const defaultMaxDialogTitleLength = 50

export function getScrollParent(node: Element): Element | null {
  const isElement = node instanceof Element
  const overflowY = isElement && window.getComputedStyle(node).overflowY
  const isScrollable = overflowY !== 'visible' && overflowY !== 'hidden'

  if (!node) {
    return null
  } else if (isScrollable && node.scrollHeight >= node.clientHeight) {
    return node
  }

  return (node.parentElement && getScrollParent(node!.parentElement)) || document.body
}

export function exists<T>(t: T | null | undefined): t is T {
  return t !== null && t !== undefined
}

export function isNumber(value?: string | number | null): value is string | number {
  return value != null && value !== '' && !isNaN(Number(value.toString()))
}

export function unescapeAndHexDecode(str: string) {
  return unescape(hexHtmlToString(str))
}

export function hexHtmlToString(str: string) {
  const REG_HEX = /&#x([a-fA-F0-9]+);/g
  return str.replace(REG_HEX, function (match, grp) {
    const num = parseInt(grp, 16)
    return String.fromCharCode(num)
  })
}

export function getCookie(name: string) {
  return document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`))?.at(2)
}

export const localeFallback = (code = 'en') => {
  const fallbacks = [code]
  const langPart = code.split('-')[0]
  if (langPart !== code) {
    fallbacks.push(langPart)
  }
  return [...fallbacks, 'en']
}
